import React, { useState, useEffect, useCallback } from 'react';
import { walletService } from '../../services/walletService';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Smartphone, RefreshCw, Loader2, Server, AlertCircle, FileText,
  CheckCircle2, MinusCircle, X,
} from 'lucide-react';
import {
  BROADCAST_EVENTS, StatePill, fmtDatetime, GlassCard, GlassAura,
} from './walletShared';

// Dimensiones de segmentación (NO por ramo). `key` apunta al breakdown del dry_run.
const DIMENSIONS = [
  { value: 'insurance_company', label: 'Compañía', key: 'by_company' },
  { value: 'product_name',      label: 'Producto', key: 'by_product' },
  { value: 'insurance_plan',    label: 'Plan',     key: 'by_plan' },
];

export default function WalletBroadcast() {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [health, setHealth] = useState(null);
  const [sync, setSync] = useState(null);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  const [event, setEvent] = useState('payment.rejected');
  const [dimension, setDimension] = useState('');   // '' | insurance_company | product_name | insurance_plan
  const [segValue, setSegValue] = useState('');     // valor del segmento
  const [basePreview, setBasePreview] = useState(null); // dry_run sin filtrar → opciones
  const [preview, setPreview] = useState(null);         // dry_run efectivo (filtrado)
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);   // resultado real de la última transmisión

  const meta = BROADCAST_EVENTS.find(e => e.value === event);
  const dimMeta = DIMENSIONS.find(d => d.value === dimension);
  const valueOptions = (dimMeta && basePreview?.breakdown?.[dimMeta.key])
    ? Object.keys(basePreview.breakdown[dimMeta.key]).sort()
    : [];
  const overviewKey = dimMeta?.key || 'by_company';
  const overview = preview?.breakdown?.[overviewKey] || {};

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const res = await walletService.listEvents({ limit: 20 });
      setEvents(res.data ?? res.events ?? []);
    } catch { setEvents([]); }
    finally { setLoadingEvents(false); }
  }, []);

  const loadSync = useCallback(async () => {
    try { setSync(await walletService.getSyncStatus()); } catch { setSync(null); }
  }, []);

  useEffect(() => {
    walletService.getHealth().then(setHealth).catch(() => setHealth(null));
    loadSync();
    loadEvents();
  }, [loadSync, loadEvents]);

  // Al cambiar de acción o dimensión, se limpia el valor seleccionado.
  useEffect(() => { setSegValue(''); }, [event, dimension]);

  // Audiencia en vivo. Sin valor → universo del evento (también puebla opciones).
  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setLastResult(null); // el resultado previo ya no aplica al nuevo objetivo
    const filters = (dimension && segValue) ? { [dimension]: segValue } : {};
    const t = setTimeout(async () => {
      try {
        const res = await walletService.broadcastEvent(event, true, filters);
        if (cancelled) return;
        setPreview(res);
        if (!segValue) setBasePreview(res);
      } catch (err) {
        if (!cancelled) toast({ title: 'Error calculando audiencia', description: err.message, variant: 'destructive' });
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [event, dimension, segValue, toast]);

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const res = await walletService.reconcile();
      toast({ title: 'Reconciliación ejecutada', description: `${res.checked} revisados · ${res.diffs} diffs · ${res.sync?.ok ?? 0} sincronizados` });
      await Promise.all([loadSync(), loadEvents()]);
    } catch (err) {
      toast({ title: 'Error en reconciliación', description: err.message, variant: 'destructive' });
    } finally { setReconciling(false); }
  };

  const handleTransmit = async () => {
    setBusy(true);
    try {
      const filters = (dimension && segValue) ? { [dimension]: segValue } : {};
      const res = await walletService.broadcastEvent(event, false, filters);
      setLastResult(res); // conteos REALES: updated / pushed / unchanged / errors
      toast({ title: 'Transmisión procesada' });
      await loadEvents();
    } catch (err) {
      toast({ title: 'Error en la transmisión', description: err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  // Interpreta el resultado real de la transmisión en lenguaje claro.
  const resultSummary = (r) => {
    if (!r) return null;
    if (r.errors > 0 && (r.updated ?? 0) === 0 && (r.pushed ?? 0) === 0)
      return { tone: 'error', Icon: AlertCircle, text: `No se pudo transmitir · ${r.errors} ${r.errors === 1 ? 'error' : 'errores'}` };
    if ((r.pushed ?? 0) > 0)
      return { tone: 'ok', Icon: CheckCircle2, text: `${r.pushed} ${r.pushed === 1 ? 'pase notificado' : 'pases notificados'} (push enviado)` };
    if ((r.updated ?? 0) > 0)
      return { tone: 'info', Icon: RefreshCw, text: `${r.updated} ${r.updated === 1 ? 'pase actualizado' : 'pases actualizados'} · sin notificación visible` };
    return { tone: 'none', Icon: MinusCircle, text: 'Sin cambios — ningún pase necesitaba esta actualización' };
  };

  const certExpiry = health?.checks?.pkpass_signing?.cert_expiry;
  const certDays = certExpiry ? Math.floor((new Date(certExpiry) - new Date()) / 86400000) : null;
  const apnsOk = health?.checks?.apns_jwt?.ok !== false;
  const degraded = (certDays != null && certDays < 30) || !apnsOk;

  return (
    <div className="relative isolate space-y-6 p-6 max-w-3xl mx-auto">
      <GlassAura />

      <div className="flex items-center gap-3">
        <span className="grid place-items-center h-11 w-11 rounded-2xl bg-orange-500/15 text-orange-500 ring-1 ring-orange-500/20 backdrop-blur-xl">
          <Bell className="h-5 w-5" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Transmisiones</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Sincronización automática y transmisión manual de pases</p>
        </div>
      </div>

      {/* 1. Sincronización automática */}
      <GlassCard className={degraded ? 'ring-red-300/60' : ''}>
        <div className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium flex items-center gap-2"><Server className="h-4 w-4" /> Sincronización automática</p>
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${degraded ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
              ● {degraded ? 'Degradada' : 'Activa'}
            </span>
          </div>
          <div className="flex flex-wrap gap-8 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Cert Apple</p>
              <p className={`font-semibold ${certDays != null && certDays < 30 ? 'text-[var(--yool-error)]' : 'text-[var(--yool-success)]'}`}>{certDays != null ? `${certDays} días` : 'OK'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">APNS</p>
              <p className={`font-semibold ${apnsOk ? 'text-[var(--yool-success)]' : 'text-[var(--yool-error)]'}`}>{apnsOk ? 'OK' : 'Error'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Última reconciliación</p>
              <p className="font-semibold">{sync?.last_run ? fmtDatetime(sync.last_run) : 'sin ejecuciones'}</p>
              {sync?.last_run && <p className="text-[10px] text-muted-foreground">{sync.checked} revisados · {sync.diffs} diffs · {sync.errors} err</p>}
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Disparadores: anulación → cancelada · cobranza → pago · reverso → actualización
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleReconcile} disabled={reconciling}>
              {reconciling ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Reconciliar ahora
            </Button>
            <Button size="sm" variant="ghost" onClick={() => navigate('/payments-admin/cron/logs')}>
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Ver logs
            </Button>
          </div>
        </div>
      </GlassCard>

      {/* 2. Transmisión manual dirigida */}
      <GlassCard>
        <div className="p-5 space-y-4">
          <p className="text-sm font-medium">Transmisión manual dirigida</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-medium mb-1 block text-muted-foreground">Acción</label>
              <Select value={event} onValueChange={setEvent}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BROADCAST_EVENTS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-muted-foreground">Segmentar por</label>
              <Select value={dimension || 'none'} onValueChange={v => setDimension(v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Ninguno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguno</SelectItem>
                  {DIMENSIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-muted-foreground">Valor</label>
              <Select value={segValue || 'all'} onValueChange={v => setSegValue(v === 'all' ? '' : v)} disabled={!dimension}>
                <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {valueOptions.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border bg-slate-50 dark:bg-slate-800/60 px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Audiencia</p>
              {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
            </div>
            {preview ? (
              <>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-bold leading-none">{preview.total_passes}</span>
                  <span className="text-sm text-muted-foreground mb-0.5">pases objetivo</span>
                </div>
                <div className="flex items-center gap-1.5 mt-2 text-sm">
                  <Smartphone className="h-3.5 w-3.5 text-[var(--yool-success)]" />
                  <span className={preview.passes_with_devices > 0 ? 'text-[var(--yool-success)] font-semibold' : 'text-muted-foreground font-semibold'}>{preview.passes_with_devices}</span>
                  <span className="text-muted-foreground">recibirán push APNS</span>
                </div>
                {Object.keys(overview).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground w-full">
                      Por {(dimMeta?.label || 'Compañía').toLowerCase()}
                    </span>
                    {Object.entries(overview).map(([k, v]) => (
                      <span key={k} className="text-[11px] rounded-full bg-white dark:bg-slate-700 border px-2 py-0.5 text-muted-foreground">{k} · <span className="font-medium text-foreground">{v}</span></span>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-1">{busy ? 'Calculando audiencia…' : 'Sin pases en este estado.'}</p>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> Solo se transmite a los pases en estado <StatePill state={meta?.target} />
          </p>

          <div className="flex justify-end">
            <Button onClick={handleTransmit} disabled={busy || !preview || preview.total_passes === 0} className="bg-orange-600 hover:bg-orange-700 text-white">
              {busy
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Enviando…</>
                : (preview?.passes_with_devices ?? 0) > 0
                  ? `Notificar a ${preview.passes_with_devices} ${preview.passes_with_devices === 1 ? 'dispositivo' : 'dispositivos'}`
                  : `Actualizar ${preview?.total_passes ?? 0} ${(preview?.total_passes ?? 0) === 1 ? 'pase' : 'pases'}`}
            </Button>
          </div>

          {/* Resultado de la transmisión — panel liquid-glass con conteos reales */}
          {lastResult && (() => {
            const s = resultSummary(lastResult);
            const toneRing = {
              ok:    'ring-emerald-300/50 dark:ring-emerald-400/20',
              info:  'ring-[#007D6E]/30 dark:ring-emerald-400/15',
              none:  'ring-amber-300/50 dark:ring-amber-400/20',
              error: 'ring-red-300/50 dark:ring-red-400/20',
            }[s.tone];
            const toneText = {
              ok: 'text-emerald-700 dark:text-emerald-300',
              info: 'text-[#007D6E] dark:text-emerald-300',
              none: 'text-amber-700 dark:text-amber-300',
              error: 'text-red-700 dark:text-red-300',
            }[s.tone];
            const stats = [
              { label: 'Actualizados', value: lastResult.updated ?? 0, cls: 'text-[#007D6E] dark:text-emerald-300' },
              { label: 'Notificados',  value: lastResult.pushed ?? 0,  cls: 'text-[var(--yool-success)] dark:text-emerald-400' },
              { label: 'Sin cambios',  value: lastResult.unchanged ?? 0, cls: 'text-slate-500 dark:text-slate-400' },
              { label: 'Errores',      value: lastResult.errors ?? 0,  cls: 'text-[var(--yool-error)] dark:text-red-400' },
            ];
            return (
              <div className={`relative rounded-2xl border border-white/50 dark:border-white/10 bg-white/70 dark:bg-slate-900/50 backdrop-blur-md ring-1 ${toneRing} shadow-lg px-4 py-3`}>
                <button
                  onClick={() => setLastResult(null)}
                  aria-label="Cerrar resultado"
                  className="absolute top-2.5 right-2.5 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <div className={`flex items-center gap-2 ${toneText}`}>
                  <s.Icon className="h-4 w-4 shrink-0" />
                  <p className="text-sm font-semibold pr-5">{s.text}</p>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-3">
                  {stats.map(st => (
                    <div key={st.label} className="rounded-lg bg-white/50 dark:bg-white/5 border border-white/40 dark:border-white/10 px-2 py-1.5 text-center">
                      <p className={`text-lg font-bold leading-none ${st.cls}`}>{st.value}</p>
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{st.label}</p>
                    </div>
                  ))}
                </div>
                {(lastResult.unchanged ?? 0) > 0 && (lastResult.updated ?? 0) === 0 && (lastResult.pushed ?? 0) === 0 && (
                  <p className="text-[11px] text-muted-foreground mt-2.5 leading-snug">
                    Estos pases ya reflejaban el estado del evento, así que no se regeneraron ni se notificaron. Es el comportamiento esperado.
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      </GlassCard>

      {/* 3. Eventos recientes */}
      <GlassCard>
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium">Eventos recientes</p>
            <Button size="icon" variant="ghost" onClick={loadEvents} disabled={loadingEvents}>
              <RefreshCw className={`h-4 w-4 ${loadingEvents ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{loadingEvents ? 'Cargando…' : 'Sin eventos recientes'}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <tbody>
                  {events.map(ev => (
                    <tr key={ev.id} className="border-b last:border-0">
                      <td className="px-2 py-2 font-mono text-muted-foreground whitespace-nowrap">{fmtDatetime(ev.created_at)}</td>
                      <td className="px-2 py-2 font-medium">{ev.event_type}</td>
                      <td className="px-2 py-2">{ev.new_state ? <StatePill state={ev.new_state} /> : '—'}</td>
                      <td className="px-2 py-2 text-right">
                        {ev.requires_push ? <span className="text-[var(--yool-success)] font-medium">push ✓</span> : ev.requires_pass_update ? <span className="text-muted-foreground">regen</span> : <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </GlassCard>
    </div>
  );
}
